import { useMutation } from '@tanstack/react-query'
import { signUp } from 'aws-amplify/auth'

import { postRequest } from 'src/lib/api-client'
import { sendEvent } from 'src/shared/api/events/send-event'
import { useEventStore } from 'src/shared/store/event-store'

type ConsumerSignUpFormData = {
  firstName?: string
  lastName?: string
  email: string
  phoneNumber?: string // Will be transformed to E.164 format by zod, but not used for auth
  password: string // Auto-generated password
  gclid?: string // Google Click ID for tracking ad campaigns
  fbclid?: string
}

type ResetUserResponse = {
  message: string
  status: string
  onboarding_complete: boolean
}

/**
 * Generates a secure random password that meets Cognito requirements
 */
const generateSecurePassword = (): string => {
  // Ensure the password contains at least one lowercase, uppercase, number, and special character
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const special = '!@#$%^&*()_+[]{}|;:,.<>?'

  // Create the password with 16 characters (4 from each category)
  let password = ''
  password += Array.from({ length: 4 }, () => lowercase[Math.floor(Math.random() * lowercase.length)]).join('')
  password += Array.from({ length: 4 }, () => uppercase[Math.floor(Math.random() * uppercase.length)]).join('')
  password += Array.from({ length: 4 }, () => numbers[Math.floor(Math.random() * numbers.length)]).join('')
  password += Array.from({ length: 4 }, () => special[Math.floor(Math.random() * special.length)]).join('')

  // Shuffle the password to make it more random
  return password
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('')
}

export const useConsumerSignUp = () => {
  const { gclid, fbclid, utmParams } = useEventStore()
  const googleAnalytics = {
    ...(gclid ? { gclid } : {}),
    ...(fbclid ? { fbclid } : {}),
    ...(utmParams?.source ? { utm_source: utmParams.source } : {}),
    ...(utmParams?.medium ? { utm_medium: utmParams.medium } : {}),
    ...(utmParams?.campaign ? { utm_campaign: utmParams.campaign } : {}),
    ...(utmParams?.term ? { utm_term: utmParams.term } : {}),
    ...(utmParams?.content ? { utm_content: utmParams.content } : {}),
    ...(utmParams?.ad ? { utm_ad: utmParams.ad } : {}),
  }

  return useMutation({
    mutationFn: async ({ firstName, lastName, email, phoneNumber }: Omit<ConsumerSignUpFormData, 'password'>) => {
      // Generate a secure random password that meets Cognito requirements
      const password = generateSecurePassword()

      try {
        // First attempt at signup
        const result = await signUp({
          username: email,
          password,
          options: {
            userAttributes: {
              ...(firstName ? { given_name: firstName } : {}),
              ...(lastName ? { family_name: lastName } : {}),
              email,
              ...(phoneNumber ? { phone_number: phoneNumber } : {}),
              'custom:onboardingComplete': 'false',
              ...(gclid ? { 'custom:gclid': gclid } : {}), // Add gclid as a custom attribute if available
            },
            autoSignIn: { enabled: false },
          },
        })

        sendEvent({
          eventType: 'auth_consumer_signup_success',
          info: {
            phone: phoneNumber,
            email,
            ...googleAnalytics,
          },
        })

        return result
      } catch (error) {
        console.error('Error during signup:', error)

        // Handle the case where the user already exists
        if (error instanceof Error && error.name === 'UsernameExistsException') {
          // Try to reset the user if they exist but are in a resettable state
          const resetResponse = (await postRequest<ResetUserResponse>('/auth/reset-consumer', {
            email,
          })) as ResetUserResponse

          if (resetResponse.status === 'UNCONFIRMED') {
            // Retry signup after successful reset
            const retryResult = await signUp({
              username: email,
              password,
              options: {
                userAttributes: {
                  ...(firstName ? { given_name: firstName } : {}),
                  ...(lastName ? { family_name: lastName } : {}),
                  email,
                  phone_number: phoneNumber,
                  'custom:onboardingComplete': 'false',
                  ...(gclid ? { 'custom:gclid': gclid } : {}), // Add gclid as a custom attribute if availableif available
                },
                autoSignIn: { enabled: false },
              },
            })

            sendEvent({
              eventType: 'auth_consumer_signup_retry',
              info: {
                email,
                phone: phoneNumber,
                ...googleAnalytics,
              },
            })

            return retryResult
          }

          throw error // Re-throw if user couldn't be reset
        }

        // Re-throw any other errors
        throw error
      }
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Signup error:', errorName, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_signup_error',
        info: {
          error: errorName,
          message: errorMessage,
          ...googleAnalytics,
        },
      })
    },
  })
}
